import { orderByHilbertCurve } from "utils/math/orderByHilbertCurve";
import { matchRoutes } from "./matchRoutes";
import { extentsCenter } from "./matching";
import { buildRouteBuckets, unionKeysInOrder } from "./typeBuckets";
import {
  buildEmptyStatusRecord,
  countStatuses,
  hasChangedItems,
  resolveTypeStatus,
} from "./typeEntries";
import type { ScaledRoute } from "../../../utils/types";
import type {
  DiffRoutesByType,
  VisualDiffRoute,
  VisualDiffTypeEntry,
} from "./types";

const sumRouteMeters = (routes: ScaledRoute[]): number =>
  routes.reduce((total, route) => total + route.meters, 0);

// Routes carry extents rather than a bbox, so their centre stands in as the position.
const orderRoutesByHilbertCurve = (
  diffRoutes: VisualDiffRoute[],
): VisualDiffRoute[] =>
  orderByHilbertCurve({
    elements: diffRoutes.map((diffRoute) => ({
      diffRoute,
      position: extentsCenter(diffRoute.route),
    })),
  }).map((entry) => entry.diffRoute);

export const diffRoutesByType: DiffRoutesByType = ({
  beforePlan,
  afterPlan,
  tolerancePdfPoints,
}) => {
  const routesByStatus = buildEmptyStatusRecord<VisualDiffRoute>();
  const routesByType = new Map<string, VisualDiffRoute[]>();
  const typeEntries: VisualDiffTypeEntry[] = [];

  const beforeBuckets = buildRouteBuckets(beforePlan, "before");
  const afterBuckets = buildRouteBuckets(afterPlan, "after");

  for (const key of unionKeysInOrder(beforeBuckets, afterBuckets)) {
    const beforeBucket = beforeBuckets.get(key);
    const afterBucket = afterBuckets.get(key);
    // Ordered here, once per plan pair, so stepping through a type never sorts at render time.
    const routes = orderRoutesByHilbertCurve(
      matchRoutes({
        beforeRoutes: beforeBucket?.routes ?? [],
        afterRoutes: afterBucket?.routes ?? [],
        tolerancePdfPoints,
        typeKey: key,
      }),
    );

    // Legend imports register types no route was ever drawn under; an empty row has nothing to show.
    if (routes.length === 0) continue;

    routesByType.set(key, routes);
    for (const route of routes) {
      routesByStatus[route.status].push(route);
    }

    const counts = countStatuses(routes);
    // A type with nothing changed still frames its own runs, so the row can zoom.
    const zoomedRoutes = hasChangedItems(counts)
      ? routes.filter((diffRoute) => diffRoute.status !== "unchanged")
      : routes;

    typeEntries.push({
      key,
      name: beforeBucket?.displayName ?? afterBucket?.displayName ?? "",
      kind: "trays",
      status: resolveTypeStatus(
        beforeBucket?.routes.length ?? 0,
        afterBucket?.routes.length ?? 0,
        counts,
      ),
      ...counts,
      metersDelta:
        sumRouteMeters(afterBucket?.routes ?? []) -
        sumRouteMeters(beforeBucket?.routes ?? []),
      // Both extents corners, so zooming frames the whole run and not just its middle.
      zoomTargets: zoomedRoutes.flatMap((diffRoute) => [
        {
          x: diffRoute.route.coordinateExtents.minX,
          y: diffRoute.route.coordinateExtents.minY,
        },
        {
          x: diffRoute.route.coordinateExtents.maxX,
          y: diffRoute.route.coordinateExtents.maxY,
        },
      ]),
    });
  }

  return { routesByStatus, routesByType, typeEntries };
};
